// The icon set. One stroke weight, one size, no colour of their own: every
// icon draws in `currentColor`, so a button decides how loud it is.
//
// Buttons that carry an icon carry no label (design §14.6). The label lives in
// `aria-label` and `title`, which is what a screen reader and a desktop
// tooltip read.

export type IconName =
  | "back"
  | "brain"
  | "book"
  | "page"
  | "clock"
  | "find"
  | "globe"
  | "share"
  | "close"
  | "copy"
  | "trash"
  | "triangle"
  | "plus"
  | "sliders"
  | "sun"
  | "moon";

interface Props {
  name: IconName;
  size?: number;
}

export function Icon({ name, size = 26 }: Props) {
  return (
    <svg
      className="icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}

/** The wordmark's mark: an eye, because the machine reads.
 *
 *  `size` is its height, and the box is cropped to the drawing, so the eye
 *  stands as tall as the letters beside it at any size. */
export function Logo({ size = 22 }: { size?: number }) {
  return (
    <svg
      className="icon logo-mark"
      width={Math.round((size * 20) / 14)}
      height={size}
      viewBox="2 5 20 14"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.45}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M2.6 12c2-3.6 5.3-6 9.4-6s7.4 2.4 9.4 6c-2 3.6-5.3 6-9.4 6s-7.4-2.4-9.4-6Z" />
      <circle cx="12" cy="12" r="2.7" fill="currentColor" stroke="none" />
    </svg>
  );
}

const PATHS: Record<IconName, React.ReactNode> = {
  back: <path d="M14.5 5 7.8 12l6.7 7" />,

  // A brain: two lobes, the fissure between them, and a fold in each.
  brain: (
    <>
      <path d="M12 5.1c-.9-1-2.4-1.4-3.7-.8-1.2.5-2 1.7-2 3-1.3.4-2.2 1.6-2.2 3 0 .7.2 1.4.7 2-.5.5-.8 1.3-.8 2 0 1.4.9 2.6 2.2 3 .2 1.6 1.5 2.8 3.2 2.8 1 0 2-.5 2.6-1.3" />
      <path d="M12 5.1c.9-1 2.4-1.4 3.7-.8 1.2.5 2 1.7 2 3 1.3.4 2.2 1.6 2.2 3 0 .7-.2 1.4-.7 2 .5.5.8 1.3.8 2 0 1.4-.9 2.6-2.2 3-.2 1.6-1.5 2.8-3.2 2.8-1 0-2-.5-2.6-1.3" />
      <path d="M12 5.1v13.7" />
      <path d="M9.1 8.6c1 .1 1.8.7 2.1 1.6" />
      <path d="M14.9 8.6c-1 .1-1.8.7-2.1 1.6" />
      <path d="M8.7 14.4c1.1.2 2 .9 2.5 1.9" />
      <path d="M15.3 14.4c-1.1.2-2 .9-2.5 1.9" />
    </>
  ),

  // An open book: the spine, and a page falling away on each side.
  book: (
    <>
      <path d="M12 7.3v12.2" />
      <path d="M12 7.3C10.4 5.9 7.8 5.3 4 5.7v11.6c3.8-.4 6.4.2 8 1.6" />
      <path d="M12 7.3c1.6-1.4 4.2-2 8-1.6v11.6c-3.8-.4-6.4.2-8 1.6" />
    </>
  ),

  // A page with a folded corner: the record of what was done.
  page: (
    <>
      <path d="M6.5 3.8h7L18 8.3v11.9H6.5z" />
      <path d="M13.5 3.8v4.5H18" />
      <path d="M9.3 12.6h5.4" />
      <path d="M9.3 15.9h5.4" />
    </>
  ),

  // A clock: the timeline of versions, read backwards.
  clock: (
    <>
      <circle cx="12" cy="12" r="8.4" />
      <path d="M12 6.8V12l3.4 2.1" />
    </>
  ),

  find: (
    <>
      <circle cx="10.6" cy="10.6" r="6.1" />
      <path d="M15.1 15.1 20 20" />
    </>
  ),

  // A globe with its equator and one meridian.
  globe: (
    <>
      <circle cx="12" cy="12" r="8.4" />
      <path d="M3.6 12h16.8" />
      <path d="M12 3.6c2.3 2.3 3.6 5.3 3.6 8.4s-1.3 6.1-3.6 8.4c-2.3-2.3-3.6-5.3-3.6-8.4S9.7 5.9 12 3.6Z" />
    </>
  ),

  share: (
    <>
      <path d="M12 3.6v10.8" />
      <path d="M8.4 7.2 12 3.6l3.6 3.6" />
      <path d="M5.4 12.6V19a1.4 1.4 0 0 0 1.4 1.4h10.4A1.4 1.4 0 0 0 18.6 19v-6.4" />
    </>
  ),

  close: (
    <>
      <path d="M6.8 6.8l10.4 10.4" />
      <path d="M17.2 6.8 6.8 17.2" />
    </>
  ),

  // Two stacked sheets: take this away with you.
  copy: (
    <>
      <path d="M9.2 9.2h9.2a1.4 1.4 0 0 1 1.4 1.4v9.2a1.4 1.4 0 0 1-1.4 1.4H9.2a1.4 1.4 0 0 1-1.4-1.4v-9.2A1.4 1.4 0 0 1 9.2 9.2Z" />
      <path d="M5.4 14.8H4.6A1.4 1.4 0 0 1 3.2 13.4V4.2a1.4 1.4 0 0 1 1.4-1.4h9.2a1.4 1.4 0 0 1 1.4 1.4V5" />
    </>
  ),

  // A waste basket. Putting a card away is a deliberate act, and an X reads
  // as closing a window rather than as deciding something.
  trash: (
    <>
      <path d="M4.4 6.6h15.2" />
      <path d="M9.6 6.6V4.8a1.2 1.2 0 0 1 1.2-1.2h2.4a1.2 1.2 0 0 1 1.2 1.2v1.8" />
      <path d="M6.4 6.6 7.3 19a1.4 1.4 0 0 0 1.4 1.3h6.6a1.4 1.4 0 0 0 1.4-1.3l.9-12.4" />
      <path d="M10.4 10.2v6.2" />
      <path d="M13.6 10.2v6.2" />
    </>
  ),

  // A solid triangle, pointing right when a card is shut and turned down by
  // CSS when it is open. Filled, because it is a state and not an action.
  triangle: <path d="M9.4 5.6 16.8 12l-7.4 6.4Z" fill="currentColor" stroke="none" />,

  plus: (
    <>
      <path d="M12 5.4v13.2" />
      <path d="M5.4 12h13.2" />
    </>
  ),

  // Sliders: the settings, without a gear's fiddly teeth.
  sliders: (
    <>
      <path d="M4 8.2h8.2" />
      <path d="M16.6 8.2H20" />
      <path d="M4 15.8h3.4" />
      <path d="M11.8 15.8H20" />
      <circle cx="14.4" cy="8.2" r="2.2" />
      <circle cx="9.6" cy="15.8" r="2.2" />
    </>
  ),

  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2.8v2.1" />
      <path d="M12 19.1v2.1" />
      <path d="M4.5 4.5 6 6" />
      <path d="M18 18l1.5 1.5" />
      <path d="M2.8 12h2.1" />
      <path d="M19.1 12h2.1" />
      <path d="M4.5 19.5 6 18" />
      <path d="M18 6l1.5-1.5" />
    </>
  ),

  moon: <path d="M20.2 14.4A8.6 8.6 0 0 1 9.6 3.8a8.6 8.6 0 1 0 10.6 10.6Z" />,
};
